import React, { useState, useRef, useEffect } from "react";
import Card from "components/card";
import ActionButton from "components/ui/ActionButton";
import SectionHeader from "components/ui/SectionHeader";
import StatusBadge from "components/ui/StatusBadge";
import { useLanguage } from "context/LanguageContext";
import { MdEdit, MdPerson } from "react-icons/md";
import { authService } from "services/authService";
import {
  userPortalService,
  MeSettingsProfile,
  MeSettingsProfileUpdate,
} from "services/userPortalService";
import FaceEnrollCard from "views/admin/profile/components/FaceEnrollCard";

const inputClass =
  "rounded-lg border border-line bg-transparent px-4 py-2.5 text-sm text-ink outline-none placeholder:text-ink-subtle focus:border-brand-500";

const labelClass = "text-sm font-medium text-ink-muted";

type SettingsForm = {
  full_name: string;
  email: string;
  username: string;
  phone: string;
  dob: string;
  gender: string;
  height: string;
  weight: string;
  bio: string;
  parent_phone: string;
  medical_note: string;
};

function profileToForm(profile: MeSettingsProfile): SettingsForm {
  return {
    full_name: profile.full_name || "",
    email: profile.email || "",
    username: profile.username || "",
    phone: profile.phone || "",
    dob: profile.dob || "",
    gender: profile.gender || "",
    height: profile.height != null ? String(profile.height) : "",
    weight: profile.weight != null ? String(profile.weight) : "",
    bio: profile.bio || "",
    parent_phone: profile.parent_phone || "",
    medical_note: profile.medical_note || "",
  };
}

const UserSettings = () => {
  const { t, locale } = useLanguage();
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [profile, setProfile] = useState<MeSettingsProfile | null>(null);
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [formBackup, setFormBackup] = useState<SettingsForm | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    userPortalService
      .getMySettingsProfile()
      .then((res) => {
        if (!active) return;
        setProfile(res);
        setForm(profileToForm(res));
      })
      .catch((err: Error) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleChange =
    (field: keyof SettingsForm) =>
    (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >
    ) => {
      setForm((prev) => {
        if (!prev) return prev;
        return { ...prev, [field]: e.target.value };
      });
    };

  const handleUploadAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    setMessage(null);
    try {
      await userPortalService.uploadAvatar(file);
      const updated = await userPortalService.getMySettingsProfile();
      setProfile(updated);
      setForm(profileToForm(updated));
      authService.getMe().catch(() => {});
      setMessage("userSettings.avatarUpdated");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "userSettings.avatarUploadError"
      );
    }
  };

  const handleEdit = () => {
    if (form) setFormBackup({ ...form });
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (formBackup) setForm({ ...formBackup });
    setIsEditing(false);
  };

  const validateForm = (f: SettingsForm): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (f.username) {
      if (/\s/.test(f.username)) {
        errors.username = "userSettings.usernameNoSpaces";
      } else if (!/^[a-zA-Z0-9._-]+$/.test(f.username)) {
        errors.username = "userSettings.usernameInvalidChars";
      } else if (f.username.length < 3) {
        errors.username = "userSettings.usernameTooShort";
      }
    }
    if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) {
      errors.email = "userSettings.emailInvalid";
    }
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;

    const errors = validateForm(form);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    setError(null);
    setMessage(null);
    const payload: MeSettingsProfileUpdate = {
      full_name: form.full_name || undefined,
      email: form.email || undefined,
      username: form.username || undefined,
      phone: form.phone || undefined,
      dob: form.dob || null,
      gender: form.gender || null,
      height: form.height ? Number(form.height) : null,
      weight: form.weight ? Number(form.weight) : null,
      bio: form.bio || null,
      parent_phone: form.parent_phone || null,
      medical_note: form.medical_note || null,
    };
    try {
      const updated = await userPortalService.updateMySettingsProfile(payload);
      setProfile(updated);
      setForm(profileToForm(updated));
      await authService.getMe();
      setMessage("userSettings.saveSuccess");
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "userSettings.saveError");
    } finally {
      setSaving(false);
    }
  };

  const genderLabel = (val: string) => {
    if (val === "male") return t("settings.male");
    if (val === "female") return t("settings.female");
    if (val === "other") return t("settings.other");
    return "—";
  };

  const formatDate = (val: string) => {
    if (!val) return "—";
    const d = new Date(val);
    return d.toLocaleDateString(locale);
  };

  if (loading) {
    return (
      <div className="mt-6 text-sm text-ink-muted">
        {t("userSettings.loading")}
      </div>
    );
  }

  if (error && !form) {
    return (
      <div className="mt-6 rounded-lg bg-red-50 p-4 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-300">
        {t(error)}
      </div>
    );
  }

  /* ========== VIEW MODE ========== */
  const renderView = () => (
    <div className="flex w-full flex-col gap-5">
      {/* Profile Header */}
      <Card extra="w-full p-5 relative">
        {profile?.center_name && (
          <div className="absolute right-4 top-4">
            <StatusBadge
              label={profile.center_name}
              tone="info"
              className="gap-1 px-3 py-1 text-xs"
            />
          </div>
        )}
        <div className="flex items-center gap-5">
          {profile?.avatar_url ? (
            <img
              src={profile.avatar_url}
              alt={t("settings.avatar")}
              className="h-20 w-20 shrink-0 rounded-full border-2 border-line object-cover"
            />
          ) : (
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-2 border-line bg-surface-muted text-ink-muted">
              <MdPerson className="h-9 w-9" />
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-ink">
              {profile?.full_name || "—"}
            </h3>
            {profile?.bio && (
              <p className="mt-0.5 text-sm text-ink-muted">
                {profile.bio}
              </p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-ink-muted">
              {profile?.email && <span>{profile.email}</span>}
              {profile?.phone && <span>{profile.phone}</span>}
              {profile?.role && (
                <span className="rounded bg-surface-muted px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                  {t(`enum.role.${profile.role}`)}
                </span>
              )}
            </div>
          </div>
          <ActionButton
            variant="secondary"
            size="sm"
            onClick={handleEdit}
            startIcon={<MdEdit className="h-4 w-4" />}
          >
            {t("settings.edit")}
          </ActionButton>
        </div>
      </Card>

      {/* Personal Info */}
      <Card extra="w-full p-6">
        <SectionHeader
          title={t("settings.personalInfo")}
          description={t("userSettings.personalInfoDesc")}
          className="mb-5"
        />
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
          <ViewField
            label={t("settings.fullName")}
            value={profile?.full_name || "—"}
          />
          <ViewField
            label={t("settings.gender")}
            value={genderLabel(profile?.gender || "")}
          />
          <ViewField
            label={t("settings.dob")}
            value={formatDate(profile?.dob || "")}
          />
          <ViewField
            label={t("settings.email")}
            value={profile?.email || "—"}
          />
          <ViewField
            label={t("userSettings.username")}
            value={profile?.username || "—"}
          />
          <ViewField
            label={t("settings.phone")}
            value={profile?.phone || "—"}
          />
          <div className="md:col-span-2">
            <ViewField label={t("settings.bio")} value={profile?.bio || "—"} />
          </div>
        </div>
      </Card>

      {/* Physical Info */}
      <Card extra="w-full p-6">
        <SectionHeader
          title={t("settings.physicalInfo")}
          description={t("userSettings.physicalInfoDesc")}
          className="mb-5"
        />
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
          <ViewField
            label={t("settings.height")}
            value={profile?.height != null ? `${profile.height} cm` : "—"}
          />
          <ViewField
            label={t("settings.weight")}
            value={profile?.weight != null ? `${profile.weight} kg` : "—"}
          />
        </div>
      </Card>

      {/* Student-specific info */}
      {profile?.role === "STUDENT" && (
        <Card extra="w-full p-6">
          <SectionHeader
            title={t("userSettings.additionalInfo")}
            description={t("userSettings.additionalInfoDesc")}
            className="mb-5"
          />
          <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
            <ViewField
              label={t("userSettings.parentPhone")}
              value={profile?.parent_phone || "—"}
            />
            <ViewField
              label={t("userSettings.medicalNote")}
              value={profile?.medical_note || "—"}
            />
          </div>
        </Card>
      )}

      {/* Face Enrollment */}
      <FaceEnrollCard />
    </div>
  );

  /* ========== EDIT MODE ========== */
  const renderEdit = () => (
    <div className="flex w-full flex-col gap-5">
      {/* Profile Header with avatar upload */}
      <Card extra="w-full p-5">
        <div className="flex items-center gap-5">
          <div className="relative shrink-0">
            {profile?.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={t("settings.avatar")}
                className="h-20 w-20 rounded-full border-2 border-line object-cover"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-line bg-surface-muted text-ink-muted">
                <MdPerson className="h-9 w-9" />
              </div>
            )}
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              className="absolute bottom-0 right-0 flex h-7 w-7 items-center justify-center rounded-full bg-brand-500 text-white shadow-md hover:bg-brand-600"
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleUploadAvatar}
            />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-ink">
              {form?.full_name || t("settings.title")}
            </h3>
          </div>
        </div>
      </Card>

      {form && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* Personal Information */}
          <Card extra="w-full p-6">
            <h4 className="mb-5 text-lg font-bold text-ink">
              {t("settings.personalInfo")}
            </h4>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>{t("settings.fullName")}</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={handleChange("full_name")}
                  placeholder={t("settings.fullNamePlaceholder")}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>{t("settings.email")}</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => {
                    handleChange("email")(e);
                    setFieldErrors((prev) => {
                      if (!prev.email) return prev;
                      const { email: _, ...rest } = prev;
                      return rest;
                    });
                  }}
                  placeholder="example@email.com"
                  className={`${inputClass} ${
                    fieldErrors.email
                      ? "border-red-400 dark:border-red-500"
                      : ""
                  }`}
                />
                {fieldErrors.email && (
                  <p className="text-xs text-red-500">{t(fieldErrors.email)}</p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>
                  {t("userSettings.username")}
                </label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\s/g, "");
                    setForm((prev) =>
                      prev ? { ...prev, username: val } : prev
                    );
                    setFieldErrors((prev) => {
                      if (!prev.username) return prev;
                      const { username: _, ...rest } = prev;
                      return rest;
                    });
                  }}
                  placeholder="username"
                  className={`${inputClass} ${
                    fieldErrors.username
                      ? "border-red-400 dark:border-red-500"
                      : ""
                  }`}
                />
                {fieldErrors.username && (
                  <p className="text-xs text-red-500">
                    {t(fieldErrors.username)}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>{t("settings.phone")}</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={handleChange("phone")}
                  placeholder={t("settings.phonePlaceholder")}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>{t("settings.gender")}</label>
                <select
                  value={form.gender}
                  onChange={handleChange("gender")}
                  className={`${inputClass} dark:bg-surface`}
                >
                  <option value="">{t("settings.gender")}</option>
                  <option value="male">{t("settings.male")}</option>
                  <option value="female">{t("settings.female")}</option>
                  <option value="other">{t("settings.other")}</option>
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>{t("settings.dob")}</label>
                <input
                  type="date"
                  value={form.dob}
                  onChange={handleChange("dob")}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-1.5 md:col-span-2">
                <label className={labelClass}>{t("settings.bio")}</label>
                <textarea
                  value={form.bio}
                  onChange={handleChange("bio")}
                  placeholder={t("settings.bioPlaceholder")}
                  rows={3}
                  className={inputClass}
                />
              </div>
            </div>
          </Card>

          {/* Physical Information */}
          <Card extra="w-full p-6">
            <h4 className="mb-5 text-lg font-bold text-ink">
              {t("settings.physicalInfo")}
            </h4>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>{t("settings.height")}</label>
                <input
                  type="number"
                  value={form.height}
                  onChange={handleChange("height")}
                  placeholder={t("settings.heightPlaceholder")}
                  className={inputClass}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className={labelClass}>{t("settings.weight")}</label>
                <input
                  type="number"
                  value={form.weight}
                  onChange={handleChange("weight")}
                  placeholder={t("settings.weightPlaceholder")}
                  className={inputClass}
                />
              </div>
            </div>
          </Card>

          {/* Student-specific fields */}
          {profile?.role === "STUDENT" && (
            <Card extra="w-full p-6">
              <h4 className="mb-5 text-lg font-bold text-ink">
                {t("userSettings.additionalInfo")}
              </h4>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>
                    {t("userSettings.parentPhone")}
                  </label>
                  <input
                    type="tel"
                    value={form.parent_phone}
                    onChange={handleChange("parent_phone")}
                    placeholder={t("userSettings.parentPhone")}
                    className={inputClass}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={labelClass}>
                    {t("userSettings.medicalNote")}
                  </label>
                  <input
                    type="text"
                    value={form.medical_note}
                    onChange={handleChange("medical_note")}
                    placeholder={t("userSettings.medicalNote")}
                    className={inputClass}
                  />
                </div>
              </div>
            </Card>
          )}

          {/* Face Enrollment */}
          <FaceEnrollCard />

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-lg border border-line px-6 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-surface-hover"
            >
              {t("action.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-brand-500 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-brand-600 active:bg-brand-700 disabled:opacity-60"
            >
              {saving ? t("action.saving") : t("settings.save")}
            </button>
          </div>
        </form>
      )}
    </div>
  );

  return (
    <div className="mt-5 flex flex-col gap-5">
      <h2 className="text-2xl font-bold text-ink">
        {t("settings.title")}
      </h2>

      {message && (
        <div className="rounded-lg bg-green-50 p-3 text-sm text-green-700 dark:bg-green-500/10 dark:text-green-300">
          {t(message)}
        </div>
      )}
      {error && form && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-300">
          {t(error)}
        </div>
      )}

      {isEditing ? renderEdit() : renderView()}
    </div>
  );
};

const ViewField = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-0.5">
    <p className="text-sm font-medium text-ink-muted">
      {label}
    </p>
    <p className="text-sm text-ink">{value || "—"}</p>
  </div>
);

export default UserSettings;
